import currency from "currency.js";
import moment from "moment";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSortBy, useTable } from "react-table";
import { GetReferralHistory200ResponseReferralHistoryInner } from "../../codegen-api";
import { TradeHistoryRow } from "../../components/FundingHistory/style";
import { Spinner } from "../../components/shared/Spinner";
import { SpinnerContainerWrapper } from "../../components/shared/Spinner/style";
import { DefaultCellForColumn } from "../../components/shared/Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../components/shared/Table/DefaultHeaderForColumn";
import { Align } from "../../components/shared/Table/style";
import { TEXT_COLORS } from "../../constants/design/colors";
import { AccountStateEnum, AuthContext } from "../../contexts/AuthContext";
import { useReferralHistory } from "../../hooks/api/referrals/useReferralHistory";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { nanosToSeconds } from "../../utils/date";
import { shortenAddress } from "../../utils/strings";
import {
  EmptyContent,
  RewardReferralTableWrapper,
  TraderWrapper,
} from "./style";

interface IReferralTableProps {
  isMobileScreen?: boolean;
}

export function ReferralTable({ isMobileScreen }: IReferralTableProps) {
  const { data: referralHistoryData, isValidating: referralHistoryLoading } =
    useReferralHistory();
  const { accountApiKeyState } = useContext(AuthContext);
  const modifiedReferralsHistory = useMemo(
    () => referralHistoryData?.referral_history || [],
    [referralHistoryData]
  );
  const { t } = useTranslation("app", {
    keyPrefix: "pages.ReferralsTab.ReferralTable",
  });

  const columns: ITableColumn<GetReferralHistory200ResponseReferralHistoryInner>[] =
    useMemo(
      () => [
        {
          title: t("trader"),
          accessor: "referee",
          Header: () => <div>{t("trader")}</div>,
          Cell: ({ row, value }: any) => (
            <Align align={"left"}>
              <TraderWrapper>
                <div>{row.original.referee_username}</div>
                <div>{shortenAddress(value)}</div>
              </TraderWrapper>
            </Align>
          ),
        },
        {
          title: t("date"),
          accessor: "created_timestamp",
          align: "right",
          Cell: ({ value }: any) => (
            <Align align={"right"} style={{ color: TEXT_COLORS.two }}>
              {moment.unix(nanosToSeconds(value)).format("DD MMM YY")}
            </Align>
          ),
        },
        {
          title: t("total_volume_traded"),
          accessor: "total_volume",
          align: "right",
          Cell: ({ value }: any) => (
            <Align align={"right"}>{currency(value).format()}</Align>
          ),
        },
      ],
      [t]
    );

  // eslint-disable-next-line operator-linebreak
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: modifiedReferralsHistory,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        },
        autoResetSortBy: false,
      } as any,
      useSortBy
    );

  return (
    <RewardReferralTableWrapper>
      {
        // eslint-disable-next-line no-nested-ternary
        modifiedReferralsHistory.length < 1 ? (
          referralHistoryLoading && !modifiedReferralsHistory ? (
            <SpinnerContainerWrapper>
              <Spinner />
            </SpinnerContainerWrapper>
          ) : (
            <EmptyContent>
              {accountApiKeyState === AccountStateEnum.OK
                ? t("no_history_desc")
                : t("connect_wallet_desc")}
            </EmptyContent>
          )
        ) : (
          <table {...getTableProps()}>
            {!isMobileScreen ? (
              <thead>
                {headerGroups.map((headerGroup) => {
                  const { key, ...trProps } = headerGroup.getHeaderGroupProps();
                  return (
                    <tr key={key} {...trProps}>
                      {headerGroup.headers.map((column: any) => {
                        const { key, ...thProps } = column.getHeaderProps(
                            column.getSortByToggleProps()
                          );
                        return (
                          // <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                          <th key={key} {...thProps}
                          >
                            {column.render("Header")}
                          </th>
                        );
                      })}
                    </tr>
                  );
                })}
              </thead>
            ) : null}
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                const { key, ...rowProps } = row.getRowProps();
                return (
                  <TradeHistoryRow key={key} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key, ...tdProps } = cell.getCellProps();
                      return (
                        <td key={key} {...tdProps}>{cell.render("Cell")}</td>
                      );
                    })}
                  </TradeHistoryRow>
                );
              })}
            </tbody>
          </table>
        )
      }
    </RewardReferralTableWrapper>
  );
}
